import { Dialog, DialogContent, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { X, Play, Pause, Volume2, Download, FileText } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { refreshConversationResourceSignedUrl } from '@/services/conversationResourceService';

interface ContentViewerModalProps {
  children: React.ReactNode;
  url: string;
  title: string;
  type: 'pdf' | 'audio' | 'video' | 'website' | 'image';
  photo?: string;
  storagePath?: string;
}

export const ContentViewerModal = ({ 
  children, 
  url, 
  title, 
  type,
  photo,
  storagePath,
}: ContentViewerModalProps) => {
  const [open, setOpen] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState(url);

  useEffect(() => {
    setResolvedUrl(url);
  }, [url]);

  const handleOpenChange = async (next: boolean) => {
    if (next && storagePath) {
      const fresh = await refreshConversationResourceSignedUrl(storagePath);
      if (fresh) setResolvedUrl(fresh);
    }
    setOpen(next);
  };
  
  const renderContent = () => {
    switch (type) {
      case 'pdf':
        return <PDFViewer url={resolvedUrl} title={title} />;
      case 'audio':
        return <AudioPlayer url={resolvedUrl} title={title} photo={photo} />;
      case 'video':
        return <VideoPlayer url={resolvedUrl} title={title} photo={photo} />;
      case 'website':
        return <WebsiteViewer url={resolvedUrl} title={title} />;
      case 'image':
        return <ImageViewer url={resolvedUrl} title={title} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => void handleOpenChange(next)}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl w-full h-[90vh] bg-white p-0 overflow-hidden flex flex-col">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {type === 'pdf' ? 'PDF document viewer' : 
           type === 'audio' ? 'Audio player' : 
           type === 'video' ? 'Video player' :
           type === 'image' ? 'Image viewer' :
           'Website viewer'}
        </DialogDescription>
        {type === 'pdf' || type === 'website' ? (
          // Minimal header for PDFs and websites to maximize content area
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 min-h-[44px] shrink-0">
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-bodhi-orange" />
              <h3 className="font-medium text-gray-900 text-sm truncate">{title}</h3>
            </div>
          </div>
        ) : type === 'image' ? (
          // Close button for images
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 left-4 z-20 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg text-gray-600 hover:text-gray-800 hover:bg-white transition-all"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        ) : (
          // Normal header for audio/video
          <div className="flex items-center justify-between p-4 border-b bg-gray-50 shrink-0">
            <div className="flex items-center space-x-3">
              {type === 'audio' && <Volume2 className="w-5 h-5 text-bodhi-orange" />}
              {type === 'video' && <Play className="w-5 h-5 text-bodhi-orange" />}
              <h3 className="font-semibold text-gray-900">{title}</h3>
            </div>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PDFViewer = ({ url, title }: { url: string; title: string }) => {
  // For external PDFs, we need to use Google Docs viewer or similar
  const pdfViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
  const directViewUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  
  const handleOpenPDF = () => {
    console.log('Opening PDF:', title);
    console.log('PDF URL:', directViewUrl);
    
    try {
      // Direct link approach - simpler and more reliable
      const link = document.createElement('a');
      link.href = directViewUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = title; // Suggest filename for download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('PDF link clicked successfully');
    } catch (error) {
      console.error('Error opening PDF:', error);
      // Fallback: show URL in console and try to copy to clipboard
      console.log('Fallback - PDF URL:', directViewUrl);
      if (navigator.clipboard) {
        navigator.clipboard.writeText(directViewUrl).then(() => {
          console.log('PDF URL copied to clipboard');
          alert('PDF URL copied to clipboard. Please paste it in your browser.');
        }).catch(() => {
          console.log('Could not copy to clipboard');
          alert('Cannot open PDF directly. Please copy this URL: ' + directViewUrl);
        });
      }
    }
  };
  
  return (
    <div className="w-full h-full relative">
      {/* Debug info in console */}
      {console.log('PDFViewer rendering:', { title, url, directViewUrl })}
      <embed
        src={url}
        type="application/pdf"
        title={title}
        className="w-full h-full border-0"
        onLoad={() => console.log('PDF embed loaded successfully:', title)}
        onError={() => console.log('PDF embed failed to load:', title)}
      />
      {/* Floating open button in top-right corner */}
      <button
        onClick={handleOpenPDF}
        className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg flex items-center space-x-2 text-bodhi-orange hover:text-bodhi-orange/80 hover:bg-white transition-all"
        title="Open PDF in new tab"
      >
        <Download className="w-4 h-4" />
        <span className="text-sm font-medium">Open in New Tab</span>
      </button>
    </div>
  );
};

const AudioPlayer = ({ url, title, photo }: { url: string; title: string; photo?: string }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const seekTime = (parseFloat(e.target.value) / 100) * duration;
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <audio ref={audioRef} src={url} preload="metadata" />
      
      {/* Album art */}
      <div className="w-48 h-48 bg-white rounded-2xl shadow-lg mb-8 flex items-center justify-center border overflow-hidden">
        {photo ? (
          <img 
            src={photo} 
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <Volume2 className="w-16 h-16 text-bodhi-orange" />
        )}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-gray-900 text-center mb-8 max-w-md">
        {title}
      </h3>

      {/* Controls */}
      <div className="w-full max-w-md space-y-4">
        {/* Progress bar */}
        <div className="flex items-center space-x-3 text-sm text-gray-600">
          <span className="w-12 text-right">{formatTime(currentTime)}</span>
          <div className="flex-1 relative">
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={handleSeek}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
          <span className="w-12">{formatTime(duration)}</span>
        </div>

        {/* Play button */}
        <div className="flex justify-center">
          <Button
            onClick={togglePlay}
            size="lg"
            className="w-16 h-16 rounded-full bg-bodhi-orange hover:bg-bodhi-orange/90 text-white"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-1" />
            )}
          </Button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #f97316;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          
          .slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #f97316;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `
      }} />
    </div>
  );
};

const VideoPlayer = ({ url, title, photo }: { url: string; title: string; photo?: string }) => {
  return (
    <div className="w-full h-full bg-black flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <video
          src={url}
          title={title}
          controls
          className="max-w-full max-h-full"
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="p-4 bg-white border-t">
        <h4 className="font-medium text-gray-900">{title}</h4>
      </div>
    </div>
  );
};

const WebsiteViewer = ({ url, title }: { url: string; title: string }) => {
  // Convert Gamma URLs to proper embed format
  let embedUrl = url;
  if (url.includes('gamma.app/docs/')) {
    const idMatch = url.match(/([a-zA-Z0-9]+)$/);
    if (idMatch) {
      embedUrl = `https://gamma.app/embed/${idMatch[1]}`;
    }
  }

  // Inline data: documents (e.g. saved Past Decisions) are same-origin generated
  // HTML; they don't need the cross-site sandbox restrictions and look cleaner
  // when allowed to render their own styles.
  const isDataDoc = embedUrl.startsWith('data:text/html');

  return (
    <div className="w-full h-full relative">
      <iframe
        src={embedUrl}
        title={title}
        className="w-full h-full border-0 absolute inset-0"
        allow="fullscreen"
        allowFullScreen
        sandbox={isDataDoc ? undefined : "allow-same-origin allow-scripts allow-forms allow-presentation"}
      />
    </div>
  );
};

const ImageViewer = ({ url, title }: { url: string; title: string }) => {
  const handleOpenInNewTab = () => {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>${title} - hey bodhi</title>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
            <link rel="icon" type="image/png" href="/AIleafGold.png" />
            <style>
              body { 
                margin: 0; 
                padding: 20px; 
                background: #f9fafb;
                font-family: Inter, system-ui, -apple-system, sans-serif;
              }
              .container {
                max-width: 1200px;
                margin: 0 auto;
                background: white;
                border-radius: 12px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                overflow: hidden;
              }
              .header {
                background: #f97316;
                color: white;
                padding: 16px 24px;
                font-size: 18px;
                font-weight: 600;
              }
              img { 
                width: 100%; 
                height: auto; 
                display: block;
              }
              @media print { 
                body { margin: 0; padding: 0; background: white; }
                .container { box-shadow: none; }
                .header { background: #f97316 !important; }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">${title}</div>
              <img src="${url}" alt="${title}" />
            </div>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  return (
    <div className="w-full h-full relative bg-white overflow-auto">
      <img
        src={url}
        alt={title}
        className="w-full h-auto min-h-full block"
      />
      {/* Open button - fixed position */}
      <button
        onClick={handleOpenInNewTab}
        className="fixed top-4 right-4 z-20 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg flex items-center space-x-2 text-bodhi-orange hover:text-bodhi-orange/80 hover:bg-white transition-all"
        title="Open in new tab"
      >
        <Download className="w-4 h-4" />
        <span className="text-sm font-medium">Open</span>
      </button>
    </div>
  );
};